import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useCan, visibleTabs, pickActiveTab, Can, NoAccess } from '../../lib/rbac';
import {
  Download, FileSpreadsheet, DollarSign, Package, Truck,
  Loader2, BarChart3, TrendingUp, TrendingDown
} from 'lucide-react';

type ReportType = 'sales' | 'margins' | 'inventory' | 'purchases';

const API_BASE = import.meta.env.VITE_API_URL || '';

export default function ReportsPage() {
  const { t } = useTranslation();
  const can = useCan();
  const [activeReport, setActiveReport] = useState<ReportType>('sales');
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { sellerId } = useUIStore();

  const reports: { key: ReportType; icon: typeof FileSpreadsheet; color: string }[] = [
    { key: 'sales', icon: FileSpreadsheet, color: 'text-blue-500' },
    { key: 'margins', icon: DollarSign, color: 'text-green-500' },
    { key: 'inventory', icon: Package, color: 'text-cyan-500' },
    { key: 'purchases', icon: Truck, color: 'text-purple-500' },
  ];

  const allowedReports = visibleTabs('reports', reports, can);
  const active = pickActiveTab('reports', reports, activeReport, can) as ReportType | null;
  useEffect(() => {
    if (active && active !== activeReport) { setActiveReport(active); setData([]); }
  }, [active, activeReport]);

  // Todo lo que pide datos usa la pestaña EFECTIVA (`active`), nunca la cruda:
  // así nunca se genera ni se baja un informe de una pestaña vedada.
  const generate = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    setData([]);
    try {
      const params = new URLSearchParams({ seller_id: String(sellerId) });
      if (active !== 'inventory') {
        params.set('date_from', dateFrom);
        params.set('date_to', dateTo);
      }
      const res = await api.get<any[]>(`/reports/${active}?${params}`);
      if (res.success && res.data) {
        setData(res.data);
        setMeta((res as any).meta || null);
      }
    } catch (e) {
      console.error('Report error:', e);
    }
    setLoading(false);
  }, [active, dateFrom, dateTo, sellerId]);

  const exportCsv = () => {
    if (!active) return;
    const params = new URLSearchParams({ seller_id: String(sellerId), format: 'csv' });
    if (active !== 'inventory') {
      params.set('date_from', dateFrom);
      params.set('date_to', dateTo);
    }
    // Get token from localStorage for auth
    const token = localStorage.getItem('cbt_token');
    const url = `${API_BASE}/api/reports/${active}?${params}`;

    fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${active}-report.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      })
      .catch(e => console.error('CSV export error:', e));
  };

  const fmtMoney = (n: number) => n != null ? `$${Number(n).toFixed(2)}` : '-';
  const fmtPct = (n: number) => n != null ? `${Number(n).toFixed(1)}%` : '-';

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[var(--color-text)]">{t('reports.title')}</h1>
      </div>

      {/* Report Type Selector */}
      <div className="flex flex-wrap gap-2">
        {allowedReports.map(r => (
          <button
            key={r.key}
            onClick={() => { setActiveReport(r.key); setData([]); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
              active === r.key
                ? 'border-[var(--color-primary)] bg-[var(--color-primary-light)] text-[var(--color-primary)]'
                : 'border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]'
            }`}
          >
            <r.icon className={`w-4 h-4 ${active === r.key ? '' : r.color}`} />
            {t(`reports.${r.key}`)}
          </button>
        ))}
      </div>

      {/* Date Filters + Generate */}
      <div className="flex flex-wrap items-end gap-3 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4">
        {active !== 'inventory' && (
          <>
            <div>
              <label className="text-xs text-[var(--color-text-secondary)] block mb-1">{t('reports.dateFrom')}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
            </div>
            <div>
              <label className="text-xs text-[var(--color-text-secondary)] block mb-1">{t('reports.dateTo')}</label>
              <input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                className="px-3 py-2 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
            </div>
          </>
        )}
        <button
          onClick={generate}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
          {t('reports.generate')}
        </button>
        {data.length > 0 && (
          <Can cap="reports.export">
            <button
              onClick={exportCsv}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
            >
              <Download className="w-4 h-4" />
              {t('reports.exportCsv')}
            </button>
          </Can>
        )}
        {meta && (
          <span className="text-sm text-[var(--color-text-secondary)]">
            {meta.total} {t('reports.rows')}
          </span>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data.length > 0 ? (
        <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
          {active === 'sales' && <SalesTable data={data} fmtMoney={fmtMoney} />}
          {active === 'margins' && <MarginsTable data={data} fmtMoney={fmtMoney} fmtPct={fmtPct} />}
          {active === 'inventory' && <InventoryTable data={data} fmtMoney={fmtMoney} />}
          {active === 'purchases' && <PurchasesTable data={data} fmtMoney={fmtMoney} />}
        </div>
      ) : meta ? (
        <div className="text-center py-12 text-[var(--color-text-secondary)]">
          <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{t('reports.noData')}</p>
        </div>
      ) : null}
    </div>
  );
}

function SalesTable({ data, fmtMoney }: { data: any[]; fmtMoney: (n: number) => string }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
        <tr>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Fecha</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Orden ML</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Producto</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">ASIN</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Site</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Comprador</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Venta</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Costo</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Profit</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Envío</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--color-border)]">
        {data.map((r, i) => (
          <tr key={i} className="hover:bg-[var(--color-bg-secondary)]">
            <td className="px-3 py-2 text-xs whitespace-nowrap">{r.date_created ? new Date(r.date_created).toLocaleDateString() : '-'}</td>
            <td className="px-3 py-2 font-mono text-xs">{r.ml_order_id}</td>
            <td className="px-3 py-2 text-xs">{r.item_title || '-'}</td>
            <td className="px-3 py-2 font-mono text-xs text-teal-600">{r.asin || '-'}</td>
            <td className="px-3 py-2 text-xs">{r.site_id || '-'}</td>
            <td className="px-3 py-2 text-xs">{r.buyer_nickname || '-'}</td>
            <td className="px-3 py-2 text-right font-medium">{fmtMoney(r.paid_amount)}</td>
            <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{r.amazon_cost ? fmtMoney(r.amazon_cost) : '-'}</td>
            <td className={`px-3 py-2 text-right font-bold ${r.profit > 0 ? 'text-green-600' : r.profit < 0 ? 'text-red-500' : ''}`}>
              {r.profit != null ? fmtMoney(r.profit) : '-'}
            </td>
            <td className="px-3 py-2 text-xs">{r.ship_status || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MarginsTable({ data, fmtMoney, fmtPct }: { data: any[]; fmtMoney: (n: number) => string; fmtPct: (n: number) => string }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
        <tr>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Producto</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">ASIN</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Ventas</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Ingresos</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Costo</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Profit</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Margen</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Costo Prom.</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Precio Prom.</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--color-border)]">
        {data.map((r, i) => (
          <tr key={i} className="hover:bg-[var(--color-bg-secondary)]">
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                {r.thumbnail && (
                  <img src={r.thumbnail} alt="" className="w-8 h-8 rounded object-cover bg-white flex-shrink-0" />
                )}
                <span className="text-xs">{r.title || '-'}</span>
              </div>
            </td>
            <td className="px-3 py-2 font-mono text-xs text-teal-600">{r.asin || '-'}</td>
            <td className="px-3 py-2 text-right">{r.total_sales}</td>
            <td className="px-3 py-2 text-right">{fmtMoney(r.total_revenue)}</td>
            <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{fmtMoney(r.total_cost)}</td>
            <td className={`px-3 py-2 text-right font-bold ${r.total_profit > 0 ? 'text-green-600' : 'text-red-500'}`}>
              {fmtMoney(r.total_profit)}
            </td>
            <td className="px-3 py-2 text-right">
              <span className={`inline-flex items-center gap-1 ${r.margin_pct >= 20 ? 'text-green-600' : r.margin_pct >= 10 ? 'text-yellow-600' : 'text-red-500'}`}>
                {r.margin_pct >= 20 ? <TrendingUp className="w-3 h-3" /> : r.margin_pct < 10 ? <TrendingDown className="w-3 h-3" /> : null}
                {fmtPct(r.margin_pct)}
              </span>
            </td>
            <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{fmtMoney(r.avg_cost)}</td>
            <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{fmtMoney(r.avg_price)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InventoryTable({ data, fmtMoney }: { data: any[]; fmtMoney: (n: number) => string }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
        <tr>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Producto</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">ASIN</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Marca</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">En Mano</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Reservado</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Disponible</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">En Tránsito</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Costo Prom.</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Valor Stock</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--color-border)]">
        {data.map((r, i) => (
          <tr key={i} className={`hover:bg-[var(--color-bg-secondary)] ${r.qty_on_hand === 0 && r.qty_incoming === 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
            <td className="px-3 py-2">
              <div className="flex items-center gap-2">
                {r.image_url && (
                  <img src={r.image_url} alt="" className="w-8 h-8 rounded object-cover bg-white flex-shrink-0" />
                )}
                <span className="text-xs">{r.title || r.asin}</span>
              </div>
            </td>
            <td className="px-3 py-2 font-mono text-xs text-teal-600">{r.asin}</td>
            <td className="px-3 py-2 text-xs text-[var(--color-text-secondary)]">{r.brand || '-'}</td>
            <td className="px-3 py-2 text-right font-medium">{r.qty_on_hand}</td>
            <td className="px-3 py-2 text-right text-orange-500">{r.qty_allocated || '-'}</td>
            <td className="px-3 py-2 text-right font-bold">{r.qty_available}</td>
            <td className="px-3 py-2 text-right text-cyan-600">{r.qty_incoming || '-'}</td>
            <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{r.avg_cost ? fmtMoney(r.avg_cost) : '-'}</td>
            <td className="px-3 py-2 text-right font-medium">{r.stock_value ? fmtMoney(r.stock_value) : '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PurchasesTable({ data, fmtMoney }: { data: any[]; fmtMoney: (n: number) => string }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
        <tr>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">PO #</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Estado</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Fecha</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Producto</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">ASIN</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Qty Ord.</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Qty Rcv.</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Costo Unit.</th>
          <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">Total Línea</th>
          <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">Tracking</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-[var(--color-border)]">
        {data.map((r, i) => (
          <tr key={i} className="hover:bg-[var(--color-bg-secondary)]">
            <td className="px-3 py-2 font-mono text-xs text-[var(--color-primary)]">{r.po_number}</td>
            <td className="px-3 py-2 text-xs">{r.fulfillment_status}</td>
            <td className="px-3 py-2 text-xs">{r.order_date ? new Date(r.order_date).toLocaleDateString() : '-'}</td>
            <td className="px-3 py-2 text-xs">{r.item_title || '-'}</td>
            <td className="px-3 py-2 font-mono text-xs text-teal-600">{r.asin || '-'}</td>
            <td className="px-3 py-2 text-right">{r.qty_ordered}</td>
            <td className="px-3 py-2 text-right text-green-600">{r.qty_received || '-'}</td>
            <td className="px-3 py-2 text-right text-[var(--color-text-secondary)]">{r.unit_cost ? fmtMoney(r.unit_cost) : '-'}</td>
            <td className="px-3 py-2 text-right font-medium">{r.line_total ? fmtMoney(r.line_total) : '-'}</td>
            <td className="px-3 py-2 text-xs font-mono">{r.tracking_number || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
